import React, { useMemo, useState } from "react";
import { InputAdornment, TextField } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import BadgeRoundedIcon from "@mui/icons-material/BadgeRounded";
import { EmptyState } from "../../../components/UI/States";
import { createCustomRole, deleteCustomRole, editCustomRole } from "./accessApi";
import { Notice, RoleChip } from "./accessUi";
import { requestSessionRefresh } from "../../../utils/liveSession";

const PEOPLE_SHOWN = 40;

// Create roles, see where every role is used, and edit/delete the custom ones.
const RolesPanel = ({ roleCatalog, roleCounts, pages, grantsByKey, users, actorId, onChanged, onOpenPage }) => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [editing, setEditing] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  // Pages each role opens: by the page's own roles or by a grant.
  const pagesByRole = useMemo(() => {
    const map = new Map();
    pages.forEach((page) => {
      const grant = grantsByKey.get(page.key);
      [...page.roles, ...(grant?.roles || [])].forEach((role) => {
        if (!map.has(role)) map.set(role, []);
        map.get(role).push({ page, viaGrant: !page.roles.includes(role) });
      });
    });
    return map;
  }, [pages, grantsByKey]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return roleCatalog;
    return roleCatalog.filter((r) => [r.name, r.description].some((f) => f.toLowerCase().includes(q)));
  }, [roleCatalog, query]);

  const trimmed = name.trim();
  const duplicate = roleCatalog.some((r) => r.name.toLowerCase() === trimmed.toLowerCase());

  const run = async (fn, success) => {
    setBusy(true);
    setMessage(null);
    try {
      await fn();
      setMessage({ type: "success", text: success });
      await onChanged();
      requestSessionRefresh();
      return true;
    } catch (err) {
      setMessage({ type: "error", text: err.message || "Couldn't save that change." });
      return false;
    } finally {
      setBusy(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!trimmed || duplicate) return;
    const ok = await run(
      () => createCustomRole(actorId, trimmed, description.trim()),
      `Created ${trimmed}. Give it to people in the User Manager, or add it to pages.`
    );
    if (ok) {
      setName("");
      setDescription("");
    }
  };

  return (
    <div className="space-y-5">
      <form onSubmit={handleCreate} className="app-card rounded-2xl border p-4 sm:p-5">
        <p className="font-semibold">Create a role</p>
        <p className="app-muted mb-3 text-sm">
          A new role does nothing until you add it to pages here and give it to people in the User Manager.
        </p>
        <div className="grid gap-3 sm:grid-cols-[minmax(0,14rem)_minmax(0,1fr)_auto]">
          <TextField
            size="small"
            label="Role name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            inputProps={{ maxLength: 50 }}
            error={Boolean(trimmed) && duplicate}
            helperText={trimmed && duplicate ? "That role already exists" : " "}
          />
          <TextField
            size="small"
            label="What it's for (optional)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            inputProps={{ maxLength: 300 }}
            helperText=" "
          />
          <button
            type="submit"
            disabled={busy || !trimmed || duplicate}
            className="app-btn app-btn-primary h-10 self-start"
          >
            <AddRoundedIcon sx={{ fontSize: 18 }} />
            Create role
          </button>
        </div>
      </form>

      <Notice message={message} />

      <div className="app-card overflow-hidden rounded-2xl border">
        <div className="app-divider flex flex-col gap-2 border-b px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-5">
          <p className="app-muted text-sm tabular-nums">
            <span className="font-semibold text-[color:var(--app-text)]">{roleCatalog.length}</span> roles ·{" "}
            <span className="font-semibold text-[color:var(--app-text)]">
              {roleCatalog.filter((r) => r.custom).length}
            </span>{" "}
            custom
          </p>
          <TextField
            size="small"
            placeholder="Search roles…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="sm:w-64"
            inputProps={{ "aria-label": "Search roles" }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </div>

        {visible.length === 0 ? (
          <EmptyState compact icon={BadgeRoundedIcon} title="No roles match" />
        ) : (
          <ul className="divide-y divide-[color:var(--app-divider)]">
            {visible.map((role) => {
              const opens = pagesByRole.get(role.name) || [];
              const count = roleCounts[role.name] || 0;
              const isOpen = expanded === role.name;
              const holders = isOpen ? users.filter((u) => (u.roles || []).includes(role.name)) : [];
              return (
                <li key={role.name}>
                  <button
                    type="button"
                    onClick={() => setExpanded(isOpen ? null : role.name)}
                    aria-expanded={isOpen}
                    className="flex w-full items-center gap-3 px-4 py-3 text-left transition hover:bg-[color:var(--app-hover)] sm:px-5"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <RoleChip role={role.name} tone={role.custom ? "custom" : "extra"} />
                        {role.custom && (
                          <span className="rounded-full bg-violet-500/15 px-2 py-0.5 text-[11px] font-semibold text-violet-700 dark:text-violet-300">
                            Custom
                          </span>
                        )}
                        {!role.custom && !role.builtIn && (
                          <span className="app-muted text-[11px]">Used by a page only</span>
                        )}
                      </div>
                      {role.description && <p className="app-muted mt-1 text-xs">{role.description}</p>}
                    </div>
                    <span className="app-muted hidden shrink-0 text-right text-xs tabular-nums sm:block">
                      {count} {count === 1 ? "person" : "people"}
                      <br />
                      {opens.length} {opens.length === 1 ? "page" : "pages"}
                    </span>
                    <ExpandMoreRoundedIcon
                      className={`app-muted shrink-0 transition ${isOpen ? "rotate-180" : ""}`}
                      fontSize="small"
                    />
                  </button>

                  {isOpen && (
                    <div className="app-surface mx-3 mb-3 space-y-4 rounded-xl border border-[color:var(--app-surface-border)] p-4 sm:mx-5">
                      <p className="app-muted text-xs sm:hidden">
                        {count} {count === 1 ? "person" : "people"} · {opens.length}{" "}
                        {opens.length === 1 ? "page" : "pages"}
                      </p>
                      <div>
                        <p className="app-eyebrow mb-2">Pages it opens</p>
                        {opens.length ? (
                          <div className="flex flex-wrap gap-1.5">
                            {opens.map(({ page, viaGrant }) => (
                              <button
                                key={page.key}
                                type="button"
                                onClick={() => onOpenPage(page.key)}
                                className="app-chip transition hover:border-sky-400/60"
                                title={viaGrant ? "Added on Page Access" : "Set in code"}
                              >
                                {page.name}
                                {viaGrant && <span className="text-sky-600 dark:text-sky-300">+</span>}
                              </button>
                            ))}
                          </div>
                        ) : (
                          <p className="app-muted text-sm">
                            No role-gated pages yet. Open a page on the Pages tab to add this role to it.
                          </p>
                        )}
                      </div>
                      <div>
                        <p className="app-eyebrow mb-2">People with it</p>
                        <p className="app-muted text-xs leading-relaxed">
                          {holders.length
                            ? holders
                                .slice(0, PEOPLE_SHOWN)
                                .map((u) => u.name)
                                .join(", ") +
                              (holders.length > PEOPLE_SHOWN ? `, and ${holders.length - PEOPLE_SHOWN} more` : "")
                            : "Nobody yet — add it from the User Manager."}
                        </p>
                      </div>

                      {role.custom && (
                        <div className="app-divider space-y-3 border-t pt-4">
                          {editing?.name === role.name ? (
                            <div className="flex flex-col gap-2 sm:flex-row">
                              <TextField
                                size="small"
                                fullWidth
                                label="What it's for"
                                value={editing.description}
                                onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                                inputProps={{ maxLength: 300 }}
                              />
                              <div className="flex gap-2">
                                <button
                                  type="button"
                                  disabled={busy}
                                  onClick={async () => {
                                    const ok = await run(
                                      () => editCustomRole(actorId, role.name, editing.description.trim()),
                                      `Updated ${role.name}.`
                                    );
                                    if (ok) setEditing(null);
                                  }}
                                  className="app-btn app-btn-sm app-btn-primary"
                                >
                                  Save
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setEditing(null)}
                                  className="app-btn app-btn-sm app-btn-ghost"
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          ) : confirmDelete === role.name ? (
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="text-sm">
                                Delete <strong>{role.name}</strong>? It comes off {count}{" "}
                                {count === 1 ? "person" : "people"} and{" "}
                                {opens.length} {opens.length === 1 ? "page" : "pages"} straight away.
                              </span>
                              <button
                                type="button"
                                disabled={busy}
                                onClick={async () => {
                                  const ok = await run(
                                    () => deleteCustomRole(actorId, role.name),
                                    `Deleted ${role.name}.`
                                  );
                                  if (ok) {
                                    setConfirmDelete(null);
                                    setExpanded(null);
                                  }
                                }}
                                className="app-btn app-btn-sm app-btn-danger"
                              >
                                Yes, delete
                              </button>
                              <button
                                type="button"
                                onClick={() => setConfirmDelete(null)}
                                className="app-btn app-btn-sm app-btn-ghost"
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <div className="flex flex-wrap gap-2">
                              <button
                                type="button"
                                onClick={() => setEditing({ name: role.name, description: role.description })}
                                className="app-btn app-btn-sm app-btn-secondary"
                              >
                                Edit description
                              </button>
                              <button
                                type="button"
                                onClick={() => setConfirmDelete(role.name)}
                                className="app-btn app-btn-sm app-btn-ghost text-rose-700 dark:text-rose-300"
                              >
                                Delete role
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

export default RolesPanel;
